import React, { useEffect, useState, useContext, useRef } from "react";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { getQuotes } from "api/Quotes";
import { trackActivity } from "api/ActivityTracker";
import TableData from "components/ui/TableData";
import { createColumnHelper } from "@tanstack/react-table";
import { Badge, Box, Flex, Image } from "theme-ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import Span from "components/typography/Span";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import { EllipsisIcon, QuestionMarkIcon } from "components/svg";
import SelectInput from "components/forms/SelectInput";
import UserContext from "context/User";
import { themevals } from "theme/themevals";
import moment from "moment-timezone";
import { regenerateQuote } from "api/QuoteExpiration";
import { useToast } from "components/toast";
import BasicModal from "components/ui/BasicModal";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";

const quoteStatusOptions = [
  { value: "0", label: "New" },
  { value: "1", label: "Accepted" },
  { value: "2", label: "Denied" },
  { value: "3", label: "Draft" },
  { value: "4", label: "Expired" },
  { value: "6", label: "Price Locked" },
];

const quoteStatusFilterOptions = [
  { value: "", label: "All statuses" },
  ...quoteStatusOptions,
];

const Quotes = () => {
  const columnHelper = createColumnHelper();
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);

  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [regenModalOpen, setRegenModalOpen] = useState<boolean>(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const hasTrackedView = useRef(false);
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");

  // `?status=0` (or `0,6`) pre-applies the status filter, so a queue row
  // can land on exactly the quotes it counted - the client-portal
  // "waiting on your answer" row uses this. Unknown codes are dropped.
  // Memoised on the raw param so TableData does not see a new object
  // every render.
  const preFilters = React.useMemo(() => {
    if (!statusParam) return undefined;
    const codes = statusParam
      .split(",")
      .map((c) => c.trim())
      .filter((c) => quoteStatusOptions.some((o) => o.value === c));
    return codes.length ? { status: `${codes.join(",")}:in` } : undefined;
  }, [statusParam]);
  const [currentQuery, setCurrentQuery] = useState<any>(null);
  const toast = useToast();

  const confirmRegenerate = (id: string) => {
    setSelectedQuoteId(id);
    setRegenModalOpen(true);
  };

  const submitRegenerate = async () => {
    if (!selectedQuoteId) return;
    setSubmitting(true);
    const res = await regenerateQuote(selectedQuoteId);
    if (res.success && res.newQuoteId) {
      setRegenModalOpen(false);
      toast.add(res.message || "Quote regenerated.", "var(--theme-ui-colors-success)");
      navigate("/quotes/" + res.newQuoteId);
    } else {
      toast.add(res.message || "Something went wrong.", "var(--theme-ui-colors-danger)");
      setRegenModalOpen(false);
    }
    setSubmitting(false);
  };

  const tableFilters = [
    {
      key: "status",
      component: SelectInput,
      condition: "in",
      props: {
        name: "status",
        label: "Status",
        options: quoteStatusFilterOptions,
        clearable: true,
        searchable: false,
        multiple: true,
        $customStyles: { minWidth: "220px" },
      },
      convertValue: (value: any) => {
        if (Array.isArray(value)) {
          if (value.every((v: any) => typeof v === "object" && v !== null && "value" in v)) return value;
          return value.map((v: any) => quoteStatusFilterOptions.find((o) => o.value === `${v}`)).filter(Boolean);
        }
        if (!value || value === "") return [];
        return `${value}`.split(":")[0].split(",").map((v: string) => quoteStatusFilterOptions.find((o) => o.value === v.trim())).filter(Boolean);
      },
      updateValue: (val: any) => {
        if (!val) return null;
        const values = Array.isArray(val) ? val : [val];
        const codes = values.map((v: any) => (typeof v === "object" && "value" in v ? v.value : `${v}`)).filter(Boolean);
        return codes.length ? codes.join(",") : null;
      },
    },
  ];

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Quotes";
    
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    
    void trackActivity({
      module: "quotes",
      action: "view_quotes_list",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: {
        page: currentQuery?.pageNum || 1,
        limit: currentQuery?.recordsPer || 15,
        filters: currentQuery?.filters || {},
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user, currentQuery]);

  const columns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => {
        const row = info.row.original;
        const mv = row?.majorVersion ?? 1;
        const mn = row?.minorVersion ?? 0;
        const suffix = (mv > 1 || mn > 0) ? (mn > 0 ? "-" + mv + "." + mn : "-" + mv) : "";
        return (
          <Box
            sx={{ fontWeight: 500, cursor: "pointer", color: "#000" }}
            onClick={() => navigate(`/quotes/${row.id}`)}
          >
            {`GMQ-${String(info.getValue()).padStart(5, "0")}${suffix}`}
          </Box>
        );
      },
      header: () => <span>Quote #</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.versionDisplay ?? (row?.minorVersion > 0 ? `v${row?.majorVersion ?? 1}.${row?.minorVersion}` : `v${row?.majorVersion ?? 1}`), {
      id: "versionDisplay",
      cell: (info: any) => (
        <Span color={themevals.colors.dkgrey} fontWeight="500">
          {info.getValue()}
        </Span>
      ),
      header: () => <span>Version</span>,
      enableSorting: false,
      meta: {
        style: { width: "80px", minWidth: "80px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.projectName ?? "Instant Estimate", {
      id: "projectName",
      cell: (info: any) => (
        <Span color="#000" fontWeight="500">
          {info.getValue()}
        </Span>
      ),
      header: () => <span>Name</span>,
      meta: {
        style: { maxWidth: "200px" },
      },
    }),
     columnHelper.accessor((row: any) => row?.primaryClient?.name, {
      id: "primaryClient.name",
      cell: (info: any) => info.getValue(),
      header: () => <span>{user.role !== null ? 'Primary Client' : 'Primary Contact'}</span>,
    }),
    ...(user.role !== null
      ? [
          columnHelper.accessor((row: any) => row?.client?.name, {
            id: "client.name",
            cell: (info: any) => info.getValue(),
            header: () => <span>Company</span>,
          }),
        ]
      : []),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => {
        const row = info.row.original;
        const expiresAt = row?.expiresAt ? new Date(row.expiresAt) : null;
        const now = new Date();
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        const isExpiringSoon = expiresAt && expiresAt > now && expiresAt <= sevenDaysFromNow && [0, 3].includes(row?.status);

        const badge = (() => {
          switch (info.getValue()) {
          case 0:
            return (
              <Badge as="span" variant="warning">
                New
              </Badge>
            );
          case 1:
            return (
              <Badge as="span" variant="success">
                Accepted
              </Badge>
            );
          case 2:
            return (
              <Badge as="span" variant="danger">
                Denied
              </Badge>
            );
          case 3:
            return (
              <Badge as="span" variant="ltgrey">
                Draft
              </Badge>
            );
          case 4:
            return (
              <Badge as="span" variant="orange">
                Expired
              </Badge>
            );
          case 5:
            return (
              <Badge as="span" variant="ltgrey">
                Superseded
              </Badge>
            );
          case 6:
            return (
              <Badge as="span" variant="info">
                Price Locked
              </Badge>
            );
          default:
            return (
              <Badge as="span" variant="warning">
                New
              </Badge>
            );
        }
        })();

        return (
          <Flex sx={{ alignItems: "center", gap: "6px" }}>
            {isExpiringSoon && (
              <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#FF7F07", flexShrink: 0 }} title={`Expires ${expiresAt?.toLocaleDateString()}`} />
            )}
            {badge}
          </Flex>
        );
      },
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor((row: any) => row?.expiresAt || row?.expiredAt, {
      id: "expiresAt",
      cell: (info: any) => {
        const row = info.row.original;
        const expiresAt = row?.expiresAt;
        const expiredAt = row?.expiredAt;
        if (row?.status === 4 && expiredAt) {
          return <Span color="var(--theme-ui-colors-danger)" fontWeight="500">{moment(expiredAt).format("MMM D, YYYY")}</Span>;
        }
        if (row?.status === 6 && row?.priceLockExpiresAt) {
          const daysLeft = Math.ceil((new Date(row.priceLockExpiresAt).getTime() - Date.now()) / 86400000);
          const lockColor = daysLeft <= 7 ? "var(--theme-ui-colors-danger)" : daysLeft <= 30 ? "#C05621" : themevals.colors.info;
          return <Span color={lockColor} fontWeight="500">{"\uD83D\uDD12 " + moment(row.priceLockExpiresAt).format("MMM D, YYYY")}</Span>;
        }
        if (expiresAt && [0, 3].includes(row?.status)) {
          const isExpiringSoon = new Date(expiresAt) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
          return <Span color={isExpiringSoon ? "#FF7F07" : themevals.colors.dkgrey} fontWeight="500">{moment(expiresAt).format("MMM D, YYYY")}</Span>;
        }
        return <Span color={themevals.colors.dkgrey}>{"\u2014"}</Span>;
      },
      header: () => <span>Expires</span>,
      meta: {
        style: { width: "120px" },
      },
    }),
    columnHelper.accessor("quoteRequest.sequence", {
      id: "quoteRequest.sequence",
      cell: (info: any) => (
        <Box
          sx={{ fontWeight: 500, cursor: "pointer", color: themevals.colors.dkgrey, textDecoration: "underline" }}
          onClick={() => window.open(`/quote-requests/${info.row.original.quoteRequest.id}`, "_blank")}
        >
          {`GMR-${String(info.getValue()).padStart(5, "0")}`}
        </Box>
      ),
      header: () => <span>RFQ #</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.items?.flat(), {
      id: "product",
      cell: (info: any) => {
        const productItem = info.getValue()?.find((item: any) => item.type === "product");
        return (
          <Span color={themevals.colors.dkgrey} fontWeight="500">
            {productItem?.name || "—"}
          </Span>
        );
      },
      header: () => <span>Product</span>,
      meta: {
        style: { maxWidth: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.items?.flat(), {
      id: "quantity",
      cell: (info: any) => {
        const productItem = info.getValue()?.find((item: any) => item.type === "product");
        if (!productItem) return <Span color={themevals.colors.dkgrey}>—</Span>;
        return (
          <Span color={themevals.colors.dkgrey} fontWeight="500">
            {`${productItem.quantity} ${productItem.unit || ""}`}
          </Span>
        );
      },
      header: () => <span>Product Qty.</span>,
      enableSorting: false,
      meta: {
        style: { maxWidth: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.updatedAt, {
      id: "updatedAt",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Last Updated</span>,
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const row = info.row.original;
        const items: { label: string; onClick: () => void; style?: React.CSSProperties }[] = [
          { label: "Details", onClick: () => navigate("/quotes/" + row.id) },
        ];

        if (row.status === 0 && user.role !== null) {
          items.push({ label: "Edit", onClick: () => navigate("/quotes/" + row.id + "/edit") });
        }

        if (row.status === 4) {
          items.push({ label: "Regenerate", onClick: () => confirmRegenerate(row.id) });
        }

        return (
          <Box>
            <DropDownMenu toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />} items={items} />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  return (
    <>
      <Header></Header>
      <Page title="Quotes" subtitle={`Manage quotes for your company.`}>
        <TableData api={getQuotes} columns={columns} refresh={refreshData} title={"All Quotes"} defaultSortBy="updatedAt" defaultSortDir="desc" nothingFoundText="No Quotes found." filters={tableFilters} preFilters={preFilters} returnQuery={(query) => setCurrentQuery(query)} />
      </Page>
      {regenModalOpen && (
        <BasicModal
          title="Regenerate Quote?"
          cancel_click={() => { setRegenModalOpen(false); setSubmitting(false); }}
          cancel_text="Cancel"
          $submitting={submitting}
          size={"sm"}
          icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
          actions={
            <BasicButton
              type="button"
              scheme="primary"
              onClick={submitRegenerate}
              $submitting={submitting}
              $iconHeight="14px"
              $iconWidth="14px"
              $loadingColor="#fff"
              keepText={true}
              styles={{ margin: "0 0.625rem .25rem 0" }}
            >
              {"Yes, regenerate"}
            </BasicButton>
          }
        >
          <P>This will create a new version of this quote repriced at current rates. The expired quote will be preserved for reference.</P>
        </BasicModal>
      )}
    </>
  );
};

export default Quotes;
